import { useCallback, useEffect, useMemo, useState } from 'react';
import { useWallet } from '@solana/wallet-adapter-react';
import { WalletReadyState } from '@solana/wallet-adapter-base';
import { AVAILABLE_WALLETS, getNetworksByWalletName, isEvmWalletInstalled, isEVMChain, } from '../../wallet';
import { ConnectionState } from '../../wallet/types';
import { SVMChain } from '../../types';
import { DEFAULT_WALLET_CONNECTION } from '../constants';
import { useWalletConnection } from './useWalletConnection';
import { useSvmConnection } from './useSvmConnection';
import { useEvmConnection } from './useEvmConnection';
export function useMultichainConnection({ toast, onWalletConnected, onWalletDisconnected, walletSession, onClearTransactionHistory, }) {
    const { walletConnection, setWalletConnection } = useWalletConnection();
    const { select, wallets } = useWallet();
    const [isWalletModalOpen, setIsWalletModalOpen] = useState(false);
    const [incomingConnection, setIncomingConnection] = useState({
        network: null,
        wallet: null,
    });
    const evm = useEvmConnection({
        walletConnection,
        setWalletConnection,
        toast,
        setIsWalletModalOpen,
    });
    const { connectors, setNewEvmConnector, resolveConnectorForWallet } = evm;
    const svm = useSvmConnection({
        walletConnection,
        setWalletConnection,
        toast,
        setIsWalletModalOpen,
        curEVMConnector: evm.curEVMConnector,
    });
    const installedWallets = useMemo(() => {
        const solWallets = wallets
            .filter((wallet) => wallet.readyState == WalletReadyState.Installed)
            .map((wallet) => wallet.adapter.name);
        return Object.keys(AVAILABLE_WALLETS).map((walletName) => ({
            name: walletName,
            icon: AVAILABLE_WALLETS[walletName].icon,
            isInstalled: solWallets.some((name) => name === walletName) || isEvmWalletInstalled(walletName),
        }));
    }, [wallets]);
    // Connect wallet with network & wallet name
    useEffect(() => {
        if (walletConnection.wallet &&
            walletConnection.network &&
            walletConnection.connectionState === ConnectionState.Disconnected) {
            console.log(`Selecting ${walletConnection.wallet} as ${walletConnection.network} wallet `);
            if (walletConnection.network === SVMChain.Solana) {
                select(walletConnection.wallet);
            }
            else if (isEVMChain(walletConnection.network)) {
                const tmpConnector = resolveConnectorForWallet(walletConnection.wallet);
                setNewEvmConnector(tmpConnector);
            }
            setWalletConnection({
                ...walletConnection,
                connectionState: ConnectionState.Connecting,
            });
        }
    }, [
        walletConnection,
        connectors,
        select,
        resolveConnectorForWallet,
        setNewEvmConnector,
        setWalletConnection,
    ]);
    // Any wallet is finally connected
    useEffect(() => {
        if (walletConnection.network &&
            walletConnection.address &&
            walletConnection.connectionState == ConnectionState.Connected) {
            if (!walletSession) {
                onWalletConnected?.({
                    walletAddress: walletConnection.address,
                    role: 'USER',
                    type: 'USER',
                });
            }
        }
    }, [walletConnection, onWalletConnected, walletSession]);
    // Finalize disconnect once both chain adapters have cleared
    useEffect(() => {
        if (walletConnection.connectionState !== ConnectionState.Disconnecting)
            return;
        const svmCleared = !svm.publicKey;
        const evmCleared = !evm.evmAddress || !evm.isConnected || evm.evmStatus === 'disconnected';
        if (svmCleared && evmCleared) {
            console.log('Disconnected from dapp wallet');
            evm.setNewEvmConnector(null);
            setWalletConnection(DEFAULT_WALLET_CONNECTION);
        }
    }, [
        walletConnection.connectionState,
        svm.publicKey,
        evm.evmAddress,
        evm.evmStatus,
        evm.isConnected,
        evm.setNewEvmConnector,
        setWalletConnection,
    ]);
    // If a connector never reports disconnected, don't leave UI stuck on Disconnecting.
    useEffect(() => {
        if (walletConnection.connectionState !== ConnectionState.Disconnecting)
            return;
        const timer = setTimeout(() => {
            console.log('Disconnected from dapp wallet (forced after timeout)');
            evm.setNewEvmConnector(null);
            setWalletConnection(DEFAULT_WALLET_CONNECTION);
        }, 2500);
        return () => clearTimeout(timer);
    }, [walletConnection.connectionState, evm.setNewEvmConnector, setWalletConnection]);
    const switchableNetwork = useMemo(() => walletConnection.wallet
        ? (getNetworksByWalletName(walletConnection.wallet).filter((chain) => chain != walletConnection.network) ?? [])
        : [], [walletConnection]);
    const handleDisconnect = useCallback(() => {
        onClearTransactionHistory?.();
        // Mark disconnecting first so finalize effect can observe account clears.
        setWalletConnection({
            ...DEFAULT_WALLET_CONNECTION,
            connectionState: ConnectionState.Disconnecting,
        });
        evm.setNewEvmConnector(null);
        if (walletConnection.network == SVMChain.Solana) {
            console.log('Disconnecting from solana');
            svm.disconnectSolana();
        }
        else if (isEVMChain(walletConnection.network)) {
            console.log('Disconnecting from evm');
            evm.disconnectEvm();
        }
        if (walletSession) {
            walletSession.disconnectWallet();
        }
        else {
            onWalletDisconnected?.();
        }
    }, [
        onClearTransactionHistory,
        setWalletConnection,
        evm.setNewEvmConnector,
        walletConnection.network,
        svm.disconnectSolana,
        evm.disconnectEvm,
        walletSession,
        onWalletDisconnected,
    ]);
    const switchNetwork = (network) => {
        toast.info(`Switching to ${network}...`, { autoClose: 1000 });
        if (isEVMChain(network) && isEVMChain(walletConnection.network)) {
            setIncomingConnection({
                network: network,
                wallet: null,
            });
            evm.switchEvmNetwork(network, walletConnection);
        }
        else {
            setIncomingConnection({
                network: network,
                wallet: walletConnection.wallet ?? null,
            });
            handleDisconnect();
        }
    };
    useEffect(() => {
        if (incomingConnection.network &&
            incomingConnection.wallet &&
            !walletConnection.network &&
            walletConnection.connectionState == ConnectionState.Disconnected) {
            setWalletConnection({
                ...walletConnection,
                network: incomingConnection.network ?? undefined,
                wallet: incomingConnection.wallet ?? undefined,
            });
            setIncomingConnection({ network: null, wallet: null });
        }
    }, [incomingConnection, walletConnection]);
    return {
        walletConnection,
        setWalletConnection,
        isWalletModalOpen,
        setIsWalletModalOpen,
        installedWallets,
        handleDisconnect,
        switchNetwork,
        switchableNetwork,
        incomingConnection,
        setIncomingConnection,
        evmAddress: evm.evmAddress,
        curEvmChain: evm.curEvmChain,
        curEVMConnector: evm.curEVMConnector,
        isConnected: evm.isConnected,
        publicKey: svm.publicKey,
        signMessage: svm.signMessage,
    };
}
